import React, { FC, useMemo } from 'react'
import {
  Button,
  Dialog,
  DialogProps,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  Box
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import { buildCompactTheme } from './compactTheme'
import { BaseScale, ModalBaseThemeContext } from './BaseScale'
import { Close } from '@mui/icons-material'
import { LoadingButton } from '../Button/LoadingButton'

interface Props extends DialogProps {
  open: boolean
  title?: string
  customTitle?: React.ReactNode
  hideHeader?: boolean
  loading?: boolean
  error?: string
  onClose?: () => void
  cancelButton?: {
    title: string
    action: () => void
    disabled?: boolean
  }
  successButton?: {
    title: string
    action: () => void
    disabled?: boolean
  }
  slim?: boolean
  footer?: React.ReactNode
  /**
   * Aplica densidad compacta al contenido del dialogo: escala tipografica
   * reducida y controles en `size='small'`. Para formularios largos que no
   * entran comodos en pantalla. Ver compactTheme.ts.
   */
  compact?: boolean
  /**
   * Reparte el alto con flex en vez de acotar el scroll con un presupuesto de
   * pixeles (que hay que recalcular a mano cada vez que cambia el header o la
   * barra de acciones): el bloque con scroll recibe exactamente el alto que
   * sobra. Va de la mano con el prop `fill` de ModalScrollableContent, que es
   * quien maneja el scroll — el contenido directo no scrollea solo.
   *
   * - `true`: el dialogo ocupa siempre todo el alto disponible. Para
   *   formularios que desbordan igual, donde conviene que el alto no cambie
   *   al plegar o desplegar secciones.
   * - `'content'`: crece con su contenido y recien ahi topa contra el alto
   *   disponible. Para formularios que a veces entran enteros — con `true`
   *   quedaria un hueco entre el ultimo campo y los botones.
   */
  fillHeight?: boolean | 'content'
}

export const Modal: FC<Props> = ({
  children,
  open,
  title,
  customTitle,
  hideHeader = false,
  loading,
  error,
  onClose,
  cancelButton,
  successButton,
  footer,
  slim = false,
  compact = false,
  fillHeight = false,
  ...modalProps
}) => {
  const styles = useStyles()

  // `fillHeight` tiene dos modos: ambos pasan el contenido a columna flex,
  // pero solo el completo le fija un alto al Paper.
  const isFlexHeight = Boolean(fillHeight)
  const isFullHeight = fillHeight === true

  const outerTheme = useTheme()
  // createTheme no es gratis y Modal se monta en toda la app: solo se
  // construye el tema cuando el modal realmente pide densidad compacta.
  const compactTheme = useMemo(
    () => (compact ? buildCompactTheme(outerTheme) : null),
    [outerTheme, compact]
  )

  // Determinar qu� t�tulo mostrar
  const renderTitleContent = () => {
    if (customTitle !== undefined) {
      return customTitle
    }
    if (title) {
      return <Typography variant='h6'>{title}</Typography>
    }
    return null
  }

  const titleContent = renderTitleContent()
  const hasTitle = titleContent !== null

  const dialog = (
    <Dialog
      fullWidth={true}
      maxWidth='sm'
      {...modalProps}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: '16px',
          // El margen baja de 32px a 16px: son 32px mas de formulario sin que
          // el dialogo toque los bordes de la ventana.
          ...(isFlexHeight && { m: 2, maxHeight: 'calc(100% - 32px)' }),
          // El Paper de MUI ya es columna flex; con un alto definido deja de
          // crecer con el contenido y pasa a repartirlo entre header,
          // contenido y acciones. Sin esto el Paper se sigue midiendo por su
          // contenido y solo topa contra el maxHeight de arriba.
          ...(isFullHeight && { height: '100%' })
        }
      }}
    >
      {!hideHeader && (
        // El header queda fuera de la densidad compacta: su alto lo fija el
        // IconButton de cerrar, asi que con `size='small'` el modal compacto
        // mostraba una barra azul mas baja que el resto de la app.
        <BaseScale>
          <DialogTitle className={styles.dialogTitle}>
            <Box
              display='flex'
              alignItems='center'
              justifyContent={hasTitle ? 'space-between' : 'flex-end'}
            >
              {titleContent}
              {onClose && (
                <IconButton onClick={onClose} style={{ color: 'white' }}>
                  <Close />
                </IconButton>
              )}
            </Box>
          </DialogTitle>
        </BaseScale>
      )}
      <DialogContent
        className={`${slim ? styles.slimDialog : ''} ${
          compact ? '' : styles.dialogContent
        }`}
        sx={{
          // MUI anula el padding superior del contenido cuando viene
          // precedido por un DialogTitle, con una regla de mayor
          // especificidad que sx — de ahi el !important (la clase JSS
          // dialogContent hace lo mismo para el caso no compacto).
          ...(compact && { paddingTop: '10px !important' }),
          ...(isFlexHeight
            ? {
                // Columna flex que se come el alto sobrante del Paper. El
                // scroll lo maneja el hijo (ModalScrollableContent con
                // `fill`), asi que aca va hidden: con `auto` aparecerian dos
                // barras, una dentro de la otra.
                //
                // La base va en `auto` y no en 0 (el `flex: 1` a secas):
                // cuando el Paper no tiene alto definido — el modo
                // 'content' — una base 0 lo dejaria medir el contenido como
                // si no ocupara lugar y el dialogo colapsaria.
                flex: '1 1 auto',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
              }
            : {
                maxHeight: 'calc(var(--app-vh) - 200px)',
                overflowY: 'auto'
              })
        }}
      >
        {children}
        {error && (
          <Typography align='right' color='error'>
            {error}
          </Typography>
        )}
      </DialogContent>
      {/* Los botones de accion tampoco se compactan: son los mismos
          Cancelar/Guardar que en cualquier otro modal. Si la pagina no pasa
          ninguno (porque arma los suyos dentro del contenido) no se renderiza
          la barra: vacia igual ocupaba su padding mas el marginTop. */}
      {(cancelButton || successButton) && (
        <BaseScale>
          <DialogActions
            className={slim ? styles.slimDialog : styles.dialogAction}
          >
            {cancelButton && (
              <Button
                variant='outlined'
                color='primary'
                onClick={cancelButton.action}
                disabled={cancelButton.disabled}
              >
                {cancelButton.title}
              </Button>
            )}
            {successButton && (
              <LoadingButton
                loading={loading}
                onClick={successButton.action}
                disabled={successButton.disabled}
              >
                {successButton.title}
              </LoadingButton>
            )}
          </DialogActions>
        </BaseScale>
      )}
      {footer}
    </Dialog>
  )

  if (!compactTheme) return dialog

  return (
    // El tema de la app viaja por contexto para que las zonas que no se
    // compactan (header, acciones) puedan volver a su escala, incluso cuando
    // las acciones las arma la pagina y viajan dentro de `children`
    // (ver ModalScrollableContent).
    <ModalBaseThemeContext.Provider value={outerTheme}>
      <ThemeProvider theme={compactTheme}>{dialog}</ThemeProvider>
    </ModalBaseThemeContext.Provider>
  )
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    dialogTitle: {
      margin: 0,
      padding: '6px 20px',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '& h6': {
        fontSize: '1rem',
        fontWeight: 450
      }
    },
    dialogAction: {
      display: 'flex',
      marginTop: 12
    },
    slimDialog: {
      padding: '0'
    },
    dialogContent: {
      paddingTop: '20px !important'
    }
  })
)
